import React, { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Container, FormText, Input } from 'reactstrap';
import { ConventionSharedModel } from '../../../../shared/convention';
import { useTranslation } from '../../../translations';
import { Fetcher, omit, useConvention, useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import {
  ConfigBooleanSetting,
  ConfigNumberBoxSetting,
  ConfigTextBoxSetting,
} from './AbstractLiveSettings';

export const RegistrationSettings: FC = () => {
  const config = useConvention();
  const result = useFetcher(async () => {
    return await api.getConvention(config.id);
  }, [config.id]);

  if (!result.success) {
    return <Fetcher result={result} />;
  }

  return <RegistrationSettingsForm initialConvention={result.data.convention} />;
};

const RegistrationSettingsForm: FC<{ readonly initialConvention: ConventionSharedModel }> = ({
  initialConvention,
}) => {
  const { ts } = useTranslation();
  const [convention, setConvention] = useState(initialConvention);
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    if (saving) {
      return;
    }

    setSaving(true);

    try {
      await api.updateConvention(initialConvention.id, omit(convention, 'id'));
      toast.success(ts('registration_settings_saved'));
    } catch (error) {
      captureError(error as Error);
    }

    setSaving(false);
  }, [saving, initialConvention.id, convention, ts]);

  const update = useCallback(
    <K extends keyof ConventionSharedModel>(key: K, value: ConventionSharedModel[K]) => {
      setConvention((old) => ({ ...old, [key]: value }));
    },
    [setConvention],
  );

  return (
    <div>
      <h3>{ts('registration')}</h3>
      <hr />
      <Container
        className={saving ? 'savingConvention clearfix' : 'clearfix'}
        id="registrationSettingsForm"
      >
        <ConfigBooleanSetting
          description="enables_disables_users_transfer"
          displayName="enable_registration_transfers"
          name="enableRegTransfer"
          saving={saving}
          update={(val) => {
            update('enableRegTransfer', val);
          }}
          value={convention.enableRegTransfer}
        />

        <ConfigBooleanSetting
          description="enable_nfc_badges_description"
          displayName="enable_nfc_badges"
          name="enableNfcBadges"
          saving={saving}
          update={(val) => {
            update('enableNfcBadges', val);
          }}
          value={convention.enableNfcBadges}
        />

        <ConfigBooleanSetting
          description="enables_disables_convention_registration"
          displayName="open_registration"
          name="registrationOpen"
          saving={saving}
          update={(val) => {
            update('registrationOpen', val);
          }}
          value={convention.registrationOpen}
        />

        <ConfigBooleanSetting
          description="settings.registration.enableqr.description"
          displayName="settings.registration.enableqr.display"
          name="enableRegQR"
          saving={saving}
          update={(val) => {
            update('enableRegQR', val);
          }}
          value={convention.enableRegQR}
        />

        <ConfigTextBoxSetting
          allowNull
          description="users_will_be_displayed_this"
          displayName="disable_refunds"
          name="disableRefunds"
          saving={saving}
          update={(val) => {
            update('disableRefunds', val);
          }}
          value={convention.disableRefunds}
        />

        <ConfigNumberBoxSetting
          allowNull
          description="limit_the_maximum_number_of"
          disabled
          displayName="limit_attendance_count"
          name="limitAttendees"
          saving={saving}
          update={(val) => {
            update('limitAttendees', val);
          }}
          value={convention.limitAttendees}
        />

        <hr />
        <h3>{ts('kiosk')}</h3>
        <ConfigBooleanSetting
          description="quikticket.description"
          displayName="quikticket.disable"
          name="disableQuikTicket"
          saving={saving}
          update={(val) => {
            update('disableQuikTicket', val);
          }}
          value={convention.disableQuikTicket}
        />

        <ConfigBooleanSetting
          description="enables_disables_accepting_payments"
          displayName="allow_online_payments_in_kiosk"
          name="kioskPaymentsEnabled"
          saving={saving}
          update={(val) => {
            update('kioskPaymentsEnabled', val);
          }}
          value={convention.kioskPaymentsEnabled}
        />
        <ConfigBooleanSetting
          description="send_user_to_the_homepage"
          displayName="kiosk_mode_timeout"
          name="kioskTimeout"
          saving={saving}
          update={(val) => {
            update('kioskTimeout', val);
          }}
          value={convention.kioskTimeout}
        />
        <ConfigBooleanSetting
          description="send_users_from_the_homepage"
          displayName="default_kiosk_mode"
          name="defaultKiosk"
          saving={saving}
          update={(val) => {
            update('kioskTimeout', val);
          }}
          value={convention.kioskTimeout}
        />

        <hr />
        <h3>{ts('cashier_ui')}</h3>

        <ConfigBooleanSetting
          description="if_badges_are_preprinted_this"
          displayName="enable_cashier_to_checkin_users"
          name="cashierCheckIn"
          saving={saving}
          update={(val) => {
            update('cashierCheckIn', val);
          }}
          value={convention.cashierCheckIn}
        />

        <ConfigBooleanSetting
          description="force_badge_reprint_reason_description"
          displayName="force_badge_reprint_reason"
          name="forceReprintReason"
          saving={saving}
          update={(val) => {
            update('forceReprintReason', val);
          }}
          value={convention.forceReprintReason}
        />

        <hr />
        <h3>{ts('settings.registration.banned_words')}</h3>
        <FormText color="muted">{ts('settings.registration.banned_words_desc')}</FormText>
        <Input
          disabled={saving}
          id="bannedWords"
          name="bannedWords"
          onChange={(e) => {
            update('bannedWords', e.currentTarget.value.split('\n'));
          }}
          rows={15}
          type="textarea"
          value={convention.bannedWords.join('\n')}
        />

        <Button
          className="float-end mt-2"
          color="primary"
          disabled={saving}
          id="saveConvention"
          onClick={async () => {
            await save();
          }}
        >
          {ts('common.save')}
        </Button>
      </Container>
    </div>
  );
};
